
import Vue from 'vue';
import { VueConstructor } from 'vue/types';
import { ErrorResponse } from '@/types/api_helper';
import { VForm } from '@/types';
import { getRoutePath, routesNames } from '@/router';
import { getHiddenPasswordAndRealPassword } from '@/_helpers/misc_helper';

import formHelper from '@/mixins/formHelper';
import UTILS from '@/store/modules/UtilityModule';

import USER from '@/store/modules/UserModule';

import PasswordForm from '@/components/auth/PasswordForm.vue';

import isValidPassword from '@/_helpers/password_validation';

import containsLowercaseCharacters from '@/_helpers/contains_lowercase_characters';
import containsNumbers from '@/_helpers/contains_numbers';
import containsUppercaseCharacters from '@/_helpers/contains_uppercase_characters';
import minTenCharacters from '@/_helpers/min_10_characters';
import twoSpecialSymbols from '@/_helpers/contains_special_symbols';
import minFiveCharacters from '@/_helpers/min_5_characters';

export default (Vue as VueConstructor<Vue & InstanceType<typeof formHelper>>).extend({
	name: 'Signup',

	components: { PasswordForm },

	mixins: [formHelper],

	data() {
		return {
			signup_form: false,

			email: '',
			password: '', // value of the input (every character is replaced by '*')
			password_value: '', // real password value
			newsletter: false,

			is_loading: false,

			routesNames: routesNames,
			getRoutePath: getRoutePath,

			isFocused: false
		};
	},

	computed: {
		USER: () => USER,
		UTILS: () => UTILS,

		isMobile(): boolean {
			return this.$vuetify.breakpoint.smAndDown;
		},

		isLoggedIn(): boolean {
			return this.USER.is_logged_in;
		},

		formSignup(): VForm {
			return this.$refs.formSignup as VForm;
		},

		passwordRequirements(): Array<{ label: any; isValid: boolean }> {
			return [
				{
					label: this.$t('alert.validation.password.min_ten_char'),
					isValid: this.passwordHasMinTenCharacters
				},
				{
					label: this.$t('alert.validation.password.lowercase_char'),
					isValid: this.passwordHasLowerCaseCharacters
				},
				{
					label: this.$t('alert.validation.password.uppercase_char'),
					isValid: this.passwordHasUppercaseCharacters
				},
				{
					label: this.$t('alert.validation.password.contains_numbers'),
					isValid: this.passwordHasNumbers
				},
				{
					label: this.$t('alert.validation.password.two_spec_symbols'),
					isValid: this.passwordHasTwoSymbols
				}
			];
		},

		passwordRequirementsCz(): Array<{ label: any; isValid: boolean }> {
			return [
				{
					label: this.$t('alert.validation.password.min_five_char'),
					isValid: this.passwordHasMinFiveCharacters
				},
				{
					label: this.$t('alert.validation.password.lowercase_char'),
					isValid: this.passwordHasLowerCaseCharacters
				},
				{
					label: this.$t('alert.validation.password.contains_numbers'),
					isValid: this.passwordHasNumbers
				}
			];
		},

		passwordHasLowerCaseCharacters(): boolean {
			return containsLowercaseCharacters(this.password_value);
		},

		passwordHasUppercaseCharacters(): boolean {
			return containsUppercaseCharacters(this.password_value);
		},

		passwordHasNumbers(): boolean {
			return containsNumbers(this.password_value);
		},

		passwordHasMinTenCharacters(): boolean {
			return minTenCharacters(this.password_value);
		},

		passwordHasMinFiveCharacters(): boolean {
			return minFiveCharacters(this.password_value);
		},

		passwordHasTwoSymbols(): boolean {
			return twoSpecialSymbols(this.password_value);
		},

		disableSignUp(): boolean {
			return !isValidPassword(this.password_value);
		},

		disableSignUpCz(): boolean {
			return !(this.passwordHasMinFiveCharacters && this.passwordHasNumbers && this.passwordHasLowerCaseCharacters);
		},

		showNewsletter(): boolean {
			return !this.$isDomain('at') && !this.$isDomain('de');
		}
	},

	methods: {
		async register(): Promise<void> {
			if (!this.formSignup.validate()) {
				return;
			}

			this.UTILS.setPageLoader(true);

			// In case user is logged in, log out
			if (this.isLoggedIn) {
				await this.USER.LOGOUT();
			}

			const params = {
				email: this.email,
				password: this.password_value,
				newsletter: this.newsletter,
				origin: 'web_signup'
			};

			const res = await this.USER.SIGNUP(params);

			if (res instanceof ErrorResponse) {
				this.UTILS.setPageLoader(false);
				this.reset();
				if (res.message === 'EMAIL_TAKEN') {
					this.showEmailTakenError();
					return;
				}
				this.showGeneralError();
				return;
			}

			this.$logger.console({ message: 'Account created' });

			if (localStorage) {
				localStorage.setItem('JStoken', res.access_token as string);
			}

			// Extract user ID from token and request USER (for /v2 - not done yet)
			// const user_id = getUserFromToken(res.access_token);

			await this.USER.LOGIN_PASSIVE(); // Checks the token, flags as Logged in and GET USER

			this.$router.push({ path: getRoutePath(routesNames.profile, this.$getDomain()) });
			this.reset();
		},

		handlePassword(input_event: Event | any): void {
			if (input_event.target && input_event.target.type === 'password') {
				// If not a password input, we can directly set the value
				this.password_value = input_event.target.value;
				return;
			}
			const new_pws = getHiddenPasswordAndRealPassword({
				input_event,
				pw_to_hide: this.password,
				real_pw: this.password_value
			});
			this.password = new_pws.new_pw_to_hide;
			this.password_value = new_pws.new_real_pw;
		},

		showGeneralError(): void {
			this.$snackbar.showMessage({
				content: this.$t('alert.error.general'),
				type: 'error'
			});
		},

		showEmailTakenError(): void {
			this.$snackbar.showMessage({
				content: this.$t('alert.error.email_taken'),
				type: 'error'
			});
		},

		reset(): void {
			this.email = '';
			this.password = '';
			this.password_value = '';
			this.formSignup.resetValidation();
			this.newsletter = false;
		},

		onFocus() {
			this.isFocused = true;
		},

		onBlur(event: any) {
			let isWrongValidation;

			if (this.$isDomain('cz')) {
				isWrongValidation = this.passwordRequirementsCz.some((item) => !item.isValid);
			} else {
				isWrongValidation = this.passwordRequirements.some((item) => !item.isValid);
			}

			if (event.relatedTarget?.tagName === 'BUTTON' || event.relatedTarget?.classList.contains('v-btn')) {
				return;
			}

			if (!isWrongValidation) {
				this.isFocused = false;
			}
		}
	}
});
